import type { AccountOverview } from "./account-overview-contract.js";

export type PayableRow = AccountOverview["rows"][number];
export type FinanceDirection = "payables" | "receivables";
export type DueBands = { overdue: string; today: string; next7: string; later: string; unknown: string };
export function payableOverdueBands(row: PayableRow, date: string, direction: FinanceDirection = "payables") {
  if (!payableBands(row, date, direction)) return null;
  const bands = { days1To15: 0n, days16To30: 0n, daysOver30: 0n };
  const entries = new Map(row.entries?.map(entry => [entry.id, entry]));
  for (const item of row.aging?.rows ?? []) {
    const entry = entries.get(item.id);
    if (!entry?.isInvoice || entry.isReturn || !item.dueDate) continue;
    const days = Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${item.dueDate}T00:00:00Z`)) / 86400000);
    if (days <= 0) continue;
    bands[days <= 15 ? "days1To15" : days <= 30 ? "days16To30" : "daysOver30"] += BigInt(item.remaining);
  }
  return { days1To15: String(bands.days1To15), days16To30: String(bands.days16To30), daysOver30: String(bands.daysOver30) };
}
export function payableBands(row: PayableRow, date: string, direction: FinanceDirection = "payables"): DueBands | null {
  if (!row.account || !row.account.reconciled || row.readError || !row.aging || row.aging.date !== date
    || row.aging.mode !== 0 || row.aging.status !== "calculated") return null;
  const signedBalance = BigInt(row.account.balance);
  const balance = direction === "payables" ? -signedBalance : signedBalance;
  if (balance < 0n) return null;
  const bands = { overdue: 0n, today: 0n, next7: 0n, later: 0n, unknown: BigInt(row.aging.otherRemaining) };
  const entries = new Map(row.entries?.map(entry => [entry.id, entry]));
  for (const item of row.aging.rows) {
    const entry = entries.get(item.id);
    if (!entry) return null;
    if (!entry.isInvoice || entry.isReturn) continue;
    const days = item.dueDate ? Math.round((Date.parse(`${item.dueDate}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`)) / 86400000) : null;
    const key = days === null ? "unknown" : days < 0 ? "overdue" : days === 0 ? "today" : days <= 7 ? "next7" : "later";
    bands[key] += BigInt(item.remaining);
  }
  const total = Object.values(bands).reduce((sum, value) => sum + value, 0n);
  if (total !== balance) return null;
  return { overdue: String(bands.overdue), today: String(bands.today), next7: String(bands.next7),
    later: String(bands.later), unknown: String(bands.unknown) };
}
